import apiService, { ServiceType } from './api';

export type InstrumentConfigStatus = 'ACTIVE' | 'PAUSED' | 'CLOSED';

/** Mirrors Prisma `ConfigType` enum on the BE (DataConfig.configType). */
export type ConfigType = 'GRID' | 'PAIR' | 'MARKET_MAKER' | 'SMART_BETA';

export interface InstrumentConfigResponse {
  id: string;
  dataConfigId: string;
  /** Set when this allocation's cash lives in another portfolio (a pair's derivatives leg). */
  portfolioId?: string | null;
  exchange: string;
  symbol: string;
  settings: Record<string, unknown>;
  initialCapital: string;
  /**
   * Capital committed to this allocation: `netDeposits + realizedCredited`. NOT the amount
   * deposited — realized PnL is folded in as the bot banks it.
   */
  currentCapital: string;
  /** Cash actually contributed: Σ SUCCESS DEPOSIT − Σ SUCCESS WITHDRAW. */
  netDeposits: string;
  /** `currentCapital − netDeposits` — realized PnL already credited here. Can be negative. */
  realizedCredited: string;
  /** Σ deposits from the account, leg-to-leg transfers excluded. Never negative. Absent on an older BE. */
  depositedTotal?: string;
  /** Σ withdrawals back to the account (write-offs and settlements included). Absent on an older BE. */
  withdrawnTotal?: string;
  /** Net of leg-to-leg transfers: `netDeposits = depositedTotal − withdrawnTotal + transferredNet`. */
  transferredNet?: string;
  holdingsCost: string;
  /** Last known held quantity (snapshot from broker). */
  heldQty: string;
  /**
   * Lazy mark-to-market: `currentCapital + heldQty × latest_matched_price`. Server
   * recomputes on every read against the Redis pricehub; falls back to
   * `currentCapital + holdingsCost` when the latest price isn't available.
   */
  marketValue?: string | null;
  /** currentCapital plus realized PnL pushed but not folded yet; what marketValue is built on. */
  effectiveCapital?: string | null;
  /**
   * Market exposure of the position alone: `|heldQty| × price`, futures multiplier
   * applied. For a derivatives leg this is the contract value (exposure, not cash) —
   * the server sends `marketValue: null` there because cash + notional is meaningless.
   */
  positionValue?: string | null;
  /**
   * Unrealized PnL (raw VND) at the latest price, signed — a short futures leg gains when
   * the price falls. Null without an entry price or a live price.
   */
  unrealizedPnl?: string | null;
  /** Avg entry price of the units still held, native unit (points for futures). */
  avgEntryPrice?: string | null;
  /** Latest matched price used by the server for marketValue (raw VND/share). */
  marketPrice?: number | null;
  status: InstrumentConfigStatus;
  createdAt: string;
  updatedAt: string;
  instrumentId?: string;
}

export interface ListInstrumentConfigFilters {
  data_config_id?: string;
  portfolio_id?: string;
  /** Scope to a single strategy family (GRID page vs PAIR page). */
  config_type?: ConfigType;
  exchange?: string;
  symbol?: string;
  status?: InstrumentConfigStatus;
  /** 1-based page number. Default 1. */
  page?: number;
  /** Page size. Default 20, server caps at 100. */
  page_size?: number;
}

export interface InstrumentConfigAggregates {
  /**
   * Σ currentCapital — capital COMMITTED, i.e. contributions plus folded realized PnL.
   * Keeps its historical name because the deposit bound subtracts it from broker cash, and
   * that arithmetic wants the committed figure. What was deposited is `totalNetDeposits`.
   */
  totalDeposited: string;
  /** Σ SUCCESS DEPOSIT − Σ SUCCESS WITHDRAW across the filter (string decimal). */
  totalNetDeposits: string;
  /** `totalDeposited − totalNetDeposits` — realized PnL credited across the filter. */
  totalRealizedCredited: string;
  /** Σ depositedTotal across the filter. Absent on an older BE. */
  totalDepositedGross?: string;
  /** Σ withdrawnTotal across the filter. Absent on an older BE. */
  totalWithdrawnGross?: string;
  /** Σ transferredNet across the filter; 0 when both ends of every transfer are inside it. */
  totalTransferredNet?: string;
  /** Σ holdingsCost across all rows matching the filter (string decimal). */
  positionCost: string;
  /** Σ marketValue across rows on the current page only (string decimal). */
  pageMarketValue: string;
}

export interface InstrumentConfigListResponse {
  items: InstrumentConfigResponse[];
  total: number;
  page: number;
  pageSize: number;
  /** Aggregates over the full filter (not just the current page). */
  aggregates: InstrumentConfigAggregates;
  /** This strategy's bot is STOPPING: deposit / withdraw answer 409 until it has stopped. */
  strategyStopping?: boolean;
}

export type TxnType = 'DEPOSIT' | 'WITHDRAW';
export type TxnStatus = 'PENDING' | 'SUCCESS' | 'FAILED' | 'CANCELLED';
/** Why the money moved. Only TRANSFER stays inside the strategy (leg to leg). */
export type TxnKind = 'USER' | 'TRANSFER' | 'WRITE_OFF' | 'SETTLEMENT' | 'ADJUSTMENT';

export interface InstrumentConfigTransaction {
  id: string;
  instrumentConfigId: string;
  type: TxnType;
  amount: string;
  status: TxnStatus;
  requestedAt: string;
  completedAt: string | null;
  failureReason: string | null;
  createdBy: string;
  /** Absent on an older BE: read as USER. */
  kind?: TxnKind;
}

export interface InstrumentConfigTransactionListResponse {
  items: InstrumentConfigTransaction[];
  totalDeposited: string;
  totalWithdrawn: string;
  /** Net of leg-to-leg transfers, kept out of the two totals above. Absent on an older BE. */
  totalTransferredNet?: string;
  pendingDeposit: string;
  pendingWithdraw: string;
  /** This allocation's bot is up: pending deposits/withdraws cannot be cancelled. */
  botActive?: boolean;
  total: number;
}

const core = () => apiService.getService(ServiceType.CORE);

export const instrumentConfigApi = {
  async list(filters: ListInstrumentConfigFilters = {}): Promise<InstrumentConfigListResponse> {
    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== null && v !== '') params[k] = String(v);
    }
    const res = await core().get<InstrumentConfigListResponse>('/instrument-config', { params });
    return res.data;
  },

  async get(id: string): Promise<InstrumentConfigResponse> {
    const res = await core().get<InstrumentConfigResponse>(`/instrument-config/${id}`);
    return res.data;
  },

  async deposit(id: string, amount: number, idempotencyKey?: string): Promise<InstrumentConfigResponse> {
    const res = await core().post<InstrumentConfigResponse>(`/instrument-config/${id}/deposit`, {
      amount,
      ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
    });
    return res.data;
  },

  async withdraw(id: string, amount: number, idempotencyKey?: string): Promise<InstrumentConfigResponse> {
    const res = await core().post<InstrumentConfigResponse>(`/instrument-config/${id}/withdraw`, {
      amount,
      ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
    });
    return res.data;
  },

  /**
   * Stop the in-flight withdraw on this InstrumentConfig. The bot cancels sell
   * orders not yet filled, keeps the unsold positions, and settles the withdraw
   * at only the cash already realized. The partial result lands shortly after
   * via the bot's callback (the PENDING txn flips to SUCCESS with the realized
   * amount). Idempotent: a no-op when there's no in-flight withdraw.
   */
  /** Cancel the PENDING withdraw — only while the bot is stopped (409 otherwise). */
  async cancelWithdraw(
    id: string,
  ): Promise<{ ok: true; requestId: string | null; cancelled?: true }> {
    const res = await core().post<{ ok: true; requestId: string | null; cancelled?: true }>(
      `/instrument-config/${id}/withdraw/cancel`,
      {},
    );
    return res.data;
  },

  /** Cancel the PENDING deposit — only while the bot is stopped (409 otherwise). */
  async cancelDeposit(
    id: string,
  ): Promise<{ ok: true; requestId: string | null; cancelled?: true }> {
    const res = await core().post<{ ok: true; requestId: string | null; cancelled?: true }>(
      `/instrument-config/${id}/deposit/cancel`,
      {},
    );
    return res.data;
  },

  /**
   * Delete (close) an allocation after it's been fully drained. The BE refuses (409)
   * unless currentCapital=0, no open positions (holdingsCost=0 & heldQty=0), and no
   * PENDING cash-op. Soft-deletes the row (status=CLOSED) — never hard-deletes.
   */
  async remove(id: string): Promise<InstrumentConfigResponse> {
    const res = await core().delete<InstrumentConfigResponse>(`/instrument-config/${id}`);
    return res.data;
  },

  async listTransactions(
    id: string,
    opts: { page?: number; page_size?: number } = {},
  ): Promise<InstrumentConfigTransactionListResponse> {
    const params: Record<string, string> = {};
    if (opts.page !== undefined) params.page = String(opts.page);
    if (opts.page_size !== undefined) params.page_size = String(opts.page_size);
    const res = await core().get<InstrumentConfigTransactionListResponse>(
      `/instrument-config/${id}/transactions`,
      { params },
    );
    return res.data;
  },

  /**
   * `availableToDeposit = cash − unspent allocations`, where `cash` is GROSS broker cash.
   *
   * Two things are deliberately not subtracted. `inHoldings` — cash already turned into
   * shares — has left the account entirely, so debiting it again shrinks the headroom as
   * the bot fills. And the cash reserved behind resting BUY orders is still part of the
   * unspent slice, which is why the bound is measured against `cash` rather than against
   * `purchasingPower` (= cash − reserved); `purchasingPower` is reported for display only.
   */
  async getDepositCapacity(portfolioId: string): Promise<{
    purchasingPower: string | null;
    cash: string | null;
    totalDeposited: string;
    inHoldings: string;
    pendingDeposits?: string;
    availableToDeposit: string | null;
    overAllocated?: string | null;
  }> {
    const res = await core().get<{
      purchasingPower: string | null;
      cash: string | null;
      totalDeposited: string;
      inHoldings: string;
      pendingDeposits?: string;
      availableToDeposit: string | null;
      overAllocated?: string | null;
    }>(`/instrument-config/deposit-capacity/${portfolioId}`);
    return res.data;
  },
};
